import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircularProgress } from "@mui/material";
import { partnerQueryKeys } from "../api/queryKeys";
import { fetchPartnerTerms } from "../api/endpoints/me";
import TermsAcceptanceDialog from "./TermsAcceptanceDialog";
import { TERMS_REQUIRED_EVENT } from "./termsEvents";

/**
 * Nothing in the portal renders until the partner has accepted the terms in force, so the dialog is
 * the first thing they see after signing in, ahead of offers or anything else. Also re-checks when
 * the server refuses a call mid-session because the office published a newer version.
 */
const TermsGate = ({ children }) => {
  const {
    data: terms,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery(partnerQueryKeys.terms(), fetchPartnerTerms, { retry: false, refetchOnMount: true });

  useEffect(() => {
    const recheck = () => refetch();
    window.addEventListener(TERMS_REQUIRED_EVENT, recheck);
    return () => window.removeEventListener(TERMS_REQUIRED_EVENT, recheck);
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !terms) {
    return (
      <div className="flex h-screen items-center justify-center p-6 text-center">
        <div>
          <h1 className="text-xl font-semibold">We couldn't load the partner terms</h1>
          <p className="mt-2 text-slate-500">
            The portal opens once your terms are confirmed. Check your connection and try again.
          </p>
          <button
            type="button"
            disabled={isFetching}
            onClick={() => refetch()}
            className="mt-4 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (terms.acceptanceRequired) return <TermsAcceptanceDialog terms={terms} />;

  return children;
};

export default TermsGate;
